import { adminMutation } from "@/admin/utils/adminMutation.ts";
import type { ApiClient } from "@/modules/api/apiClient.ts";
import { getResponseData } from "@/modules/api/apiClient.ts";
import { atomAllLocationsQuery } from "@/modules/location/atomAllLocationsQuery.ts";
import { jsonEncodedBody } from "@/utils/fetchClient.ts";
import { atomFamilyMulti } from "@/utils/jotai/atomFamilyMulti.ts";
import { buildQueryKey } from "@/utils/query/buildQueryKey.ts";
import { atomFromStandardQueryOptions } from "@/utils/query/queryAtom.ts";
import type { QueryKey } from "@tanstack/react-query";
import { queryOptions } from "@tanstack/react-query";
import type {
    AdminVirtualMachine,
    VmActionLogResponse,
    VmAdminStatistics,
    VmDesignatedBody,
    VmIftuneBody,
    VmMigrateBody,
    VmModifyBody,
    VmResetPasswordBody,
    VmResizeDiskBody,
    VmStartCustomRescueBody,
} from "@warrenio/api-spec/spec.oats.gen";

interface Params {
    location: string;
    uuid?: string;
}

function getQueryVmKey(params?: Params): QueryKey {
    return params == null ? ["user-resource/admin/vm"] : ["user-resource/admin/vm", params.uuid, params.location];
}

function getQueryStatisticsKey(params?: Params): QueryKey {
    return params == null
        ? ["base-operator/admin/vm/statistics/chart"]
        : ["base-operator/admin/vm/statistics/chart", params.uuid, params.location];
}

export interface VmAdminStatisticsLoc extends VmAdminStatistics, Params {}

function statisticsFromData(data: VmAdminStatistics, location: string): VmAdminStatisticsLoc {
    return { ...data, location };
}

interface ActionLogParams {
    location: string;
    guest_uuid?: string;
}

function getLogQueryKey(params: ActionLogParams): QueryKey {
    return buildQueryKey("base-operator/admin/action_log", params.location, params.guest_uuid);
}

function getSingleQuery(apiClient: ApiClient, params: ActionLogParams) {
    return queryOptions({
        queryKey: getLogQueryKey(params),
        queryFn: async ({ signal }): Promise<VmActionLogResponse> => {
            const { location, guest_uuid } = params;
            return getResponseData(
                await apiClient.GET("/{location}/base-operator/admin/action_log", {
                    signal,
                    params: { query: { guest_uuid }, path: { location } },
                }),
            );
        },
    });
}

export const vmActionLogQueryAtom = atomFamilyMulti((location: string, guest_uuid: string) =>
    atomFromStandardQueryOptions(getSingleQuery, { location, guest_uuid }),
);

function getSingleVmQuery(apiClient: ApiClient, params: { location: string; uuid: string }) {
    return queryOptions({
        queryKey: getQueryVmKey(params),
        queryFn: async ({ signal }): Promise<AdminVirtualMachine> => {
            const { location, uuid } = params;
            return getResponseData(
                await apiClient.GET("/{location}/user-resource/admin/vm", {
                    signal,
                    params: { query: { uuid }, path: { location } },
                }),
            );
        },
    });
}

export const vmQueryAtom = atomFamilyMulti((location: string, uuid: string) =>
    atomFromStandardQueryOptions(getSingleVmQuery, { location, uuid }),
);

function getSingleStatisticsQuery(apiClient: ApiClient, params: Params) {
    return queryOptions({
        queryKey: getQueryStatisticsKey(params),
        queryFn: async ({ signal }): Promise<VmAdminStatisticsLoc> => {
            const { location } = params;
            const response = getResponseData(
                await apiClient.GET("/{location}/base-operator/admin/vm/statistics/chart", {
                    signal,
                    params: { path: { location } },
                }),
            );

            return statisticsFromData(response, location);
        },
    });
}

const statisticsQueryAtom = atomFamilyMulti((location: string) =>
    atomFromStandardQueryOptions(getSingleStatisticsQuery, { location }),
);

export const adminAllStatisticsQueryAtom = atomAllLocationsQuery(statisticsQueryAtom, (results) => results);

//#region Mutations

const entity = "Virtual machine";

export const startVmMutation = adminMutation((apiClient) => ({
    entity,
    operation: "start",
    requestFn: async ({ uuid, location }: { uuid: string; location: string }) =>
        await apiClient.POST("/{location}/user-resource/admin/vm/start", {
            ...jsonEncodedBody,
            body: { uuid },
            params: { path: { location } },
        }),
}));

export const stopVmMutation = adminMutation((apiClient) => ({
    entity,
    operation: "stop",
    requestFn: async ({ uuid, location, force }: { uuid: string; location: string; force: boolean }) =>
        await apiClient.POST("/{location}/user-resource/admin/vm/stop", {
            ...jsonEncodedBody,
            body: { uuid, force },
            params: { path: { location } },
        }),
}));

export const deleteVmMutation = adminMutation((apiClient) => ({
    entity,
    operation: "delete",
    requestFn: async ({ uuid, location }: { uuid: string; location: string }) =>
        await apiClient.DELETE("/{location}/user-resource/admin/vm", {
            ...jsonEncodedBody,
            body: { uuid },
            params: { path: { location } },
        }),
}));

export const migrateVmMutation = adminMutation((apiClient) => ({
    entity,
    operation: "migrate",
    requestFn: async ({ body, location }: { body: VmMigrateBody; location: string }) =>
        await apiClient.POST("/{location}/base-operator/migrate", {
            ...jsonEncodedBody,
            body,
            params: { path: { location } },
        }),
}));

export const updateNicIftuneMutation = adminMutation((apiClient) => ({
    entity,
    operation: "update",
    requestFn: async ({ body, location }: { body: VmIftuneBody; location: string }) =>
        await apiClient.PUT("/{location}/base-operator/admin/vm/iftune", {
            ...jsonEncodedBody,
            body,
            params: { path: { location } },
        }),
}));

export const designatedVmMutation = adminMutation((apiClient) => ({
    entity,
    operation: "migrate",
    requestFn: async ({ body, location }: { body: VmDesignatedBody; location: string }) =>
        await apiClient.PATCH("/{location}/user-resource/admin/vm/designated_pool", {
            ...jsonEncodedBody,
            body,
            params: { path: { location } },
        }),
}));

export const modifyVmMutation = adminMutation((apiClient) => ({
    entity,
    operation: "update",
    requestFn: async ({ body, location }: { body: VmModifyBody; location: string }) =>
        await apiClient.PATCH("/{location}/base-operator/admin/vm", {
            ...jsonEncodedBody,
            body,
            params: { path: { location } },
        }),
}));

export const resizeDiskMutation = adminMutation((apiClient) => ({
    entity,
    operation: "update",
    requestFn: async ({ body, location }: { body: VmResizeDiskBody; location: string }) =>
        await apiClient.PATCH("/{location}/base-operator/admin/vm/storage", {
            ...jsonEncodedBody,
            body,
            params: { path: { location } },
        }),
}));

export const rescueModeMutation = adminMutation((apiClient) => ({
    entity,
    operation: "update",
    requestFn: async ({ body, location }: { body: VmStartCustomRescueBody; location: string }) =>
        await apiClient.POST("/{location}/user-resource/admin/vm/boot_iso_media", {
            ...jsonEncodedBody,
            body,
            params: { path: { location } },
        }),
}));

export const resetPasswordMutation = adminMutation((apiClient) => ({
    entity,
    operation: "reset",
    requestFn: async ({ body, location }: { body: VmResetPasswordBody; location: string }) =>
        await apiClient.POST("/{location}/base-operator/admin/vm/user/generate_password", {
            ...jsonEncodedBody,
            body,
            params: { path: { location } },
        }),
}));
//#endregion Mutations
